import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap, map } from 'rxjs/operators';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { HttpService } from '../http/http.service';
import { LoginLocation } from '../model/authentication/authentication-reponse-dpo';
import { OpwLocation, OpwLocationWithStats } from '../model/location/OpwLocation';

@Injectable({
  providedIn: 'root',
})
export class LocationService {
  private readonly AVAILABLE_LOCATIONS: string = 'available_locations';
  private cachedLocationsWithStats: OpwLocationWithStats[] | null = null;

  constructor(
    private localStorageService: LocalStorageService,
    private httpService: HttpService,
  ) {}

  public setUserOsmozisLocations(locations: LoginLocation[]) {
    this.localStorageService.save<LoginLocation[]>(this.AVAILABLE_LOCATIONS, locations);
  }
  public getUserOsmozisLocations(): LoginLocation[] {
    return this.localStorageService.get<LoginLocation[]>(this.AVAILABLE_LOCATIONS) ?? [];
  }

  public hasUserOsmozisAvailableLocation(locationId: string): boolean {
    return this.getUserOsmozisLocations().some((location) => location.id.toString() == locationId);
  }

  public getLocations(): Observable<OpwLocation[]> {
    return this.httpService.getApiResponseBody<OpwLocation[]>('/locations');
  }

  public getLocationsWithStats(): Observable<OpwLocationWithStats[]> {
    return this.httpService.getApiResponseBody<OpwLocationWithStats[]>('/locationsWithStats');
  }

  /**
   * Crée ou met à jour une location via l'API
   * @param locationData - Données de la location à créer/modifier
   * @returns Observable<LocationApi> - La location créée/modifiée
   */
  public createOrUpdateLocation(locationData: OpwLocation): Observable<OpwLocation> {
    return this.httpService.putAndGetApiResponseBody<OpwLocation>('/locations', locationData).pipe(
      tap(() => {
        this.resetCachedUserOpwLocationsWithStats();
      }),
    );
  }

  /**
   * Supprime une location via l'API
   * @param locationId - ID de la location à supprimer
   * @returns Observable<any> - Observable de la réponse
   */
  public deleteLocation(locationId: number): Observable<any> {
    return this.httpService.delete(`/locations/${locationId}`);
  }

  /**
   * Remet à zéro le cache des locations
   * La prochaine demande via getCachedLocations() récupérera les données depuis l'API
   */
  public resetCachedUserOpwLocationsWithStats(): void {
    this.cachedLocationsWithStats = null;
  }

  public getCachedUserOpwLocationsWithStats(): Observable<OpwLocationWithStats[]> {
    if (this.cachedLocationsWithStats) {
      return of(this.cachedLocationsWithStats);
    }

    return this.getLocationsWithStats().pipe(
      map((locations: OpwLocationWithStats[]) =>
        locations.map((entry) => ({
          ...entry,
          location: {
            ...entry.location,
            isRemoteTimezone:
              !!entry.location.timezone && Intl.DateTimeFormat().resolvedOptions().timeZone !== entry.location.timezone,
          },
        })),
      ),
      tap((locations: OpwLocationWithStats[]) => {
        this.cachedLocationsWithStats = locations;
      }),
    );
  }

  public getCachedOpwLocationStatsFromId(locationId: number): Observable<OpwLocationWithStats | undefined> {
    return this.getCachedUserOpwLocationsWithStats().pipe(
      map((locations: OpwLocationWithStats[]) => {
        return locations.find((locationWithStats) => locationWithStats.location.locationId === locationId);
      }),
    );
  }

  public getCachedOpwLocationFromId(locationId: number): Observable<OpwLocation | undefined> {
    return this.getCachedOpwLocationStatsFromId(locationId).pipe(
      map((locationWithStats) => locationWithStats?.location),
    );
  }
}
